import api from "config/axiosConfig";
import { apiRoutes } from "util/apiRoutes";
import {
  buildIntrinsicDimensions,
  extractIntrinsicDimensions,
  isSaneAspectRatio,
  type IntrinsicDimensions,
} from "util/mediaAspect";

export type UploadableEntityType =
  | "impact"
  | "teabate"
  | "solution"
  | "action"
  | "comment"
  | "feedback"
  | "collection";

export interface LocalMediaUploadInput {
  file: File;
  order: number;
  /**
   * Pre-resolved intrinsic dimensions. When provided, the upload pipeline
   * forwards `width`, `height`, and `aspectRatio` to the server's finalize
   * call. When omitted, the helper attempts a lightweight measurement via
   * `extractIntrinsicDimensions(file)` (with a short timeout) so callers
   * that have not pre-resolved dimensions still benefit from accurate
   * card-level layout.
   */
  intrinsic?: IntrinsicDimensions;
}

interface PresignUploadDescriptor {
  key: string;
  uploadUrl: string;
  method: "PUT";
  headers: Record<string, string>;
  mimetype: string;
  originalName: string;
  size: number;
  order: number;
  type: "image" | "video";
}

interface PresignResponse {
  uploads: PresignUploadDescriptor[];
  expiresIn: number;
}

const normalizeEntityType = (entityType: string): UploadableEntityType => {
  const value = entityType.toLowerCase().trim();
  if (value === "impacts" || value === "impact") return "impact";
  if (value === "teabate" || value === "teabates") return "teabate";
  if (value === "solutions" || value === "solution") return "solution";
  if (value === "actions" || value === "action") return "action";
  if (value === "comments" || value === "comment") return "comment";
  if (value === "feedbacks" || value === "feedback") return "feedback";
  if (value === "collections" || value === "collection") return "collection";
  throw new Error(`Unsupported entity type for media upload: ${entityType}`);
};

const resolveIntrinsic = async (
  input: LocalMediaUploadInput,
): Promise<IntrinsicDimensions | null> => {
  if (input.intrinsic) {
    return input.intrinsic;
  }
  try {
    const dims = await extractIntrinsicDimensions(input.file);
    if (dims) return dims;
  } catch {
    // Measurement is best-effort; the server tolerates missing fields.
  }
  return null;
};

interface FinalizeMediaPayloadEntry {
  key: string;
  order: number;
  originalName: string;
  mimetype: string;
  size: number;
  type: "image" | "video";
  width?: number;
  height?: number;
  aspectRatio?: number;
}

const buildFinalizeEntry = (
  upload: PresignUploadDescriptor,
  intrinsic: IntrinsicDimensions | null,
): FinalizeMediaPayloadEntry => {
  const base: FinalizeMediaPayloadEntry = {
    key: upload.key,
    order: upload.order,
    originalName: upload.originalName,
    mimetype: upload.mimetype,
    size: upload.size,
    type: upload.type,
  };
  if (intrinsic && isSaneAspectRatio(intrinsic.aspectRatio)) {
    base.width = intrinsic.width;
    base.height = intrinsic.height;
    base.aspectRatio = intrinsic.aspectRatio;
  }
  return base;
};

export async function uploadEntityMediaDirect(
  entityTypeInput: string,
  entityId: number,
  mediaInputs: LocalMediaUploadInput[],
): Promise<void> {
  if (mediaInputs.length === 0) {
    return;
  }
  const entityType = normalizeEntityType(entityTypeInput);
  const presignPayload = {
    files: mediaInputs.map((entry) => ({
      name: entry.file.name,
      type: entry.file.type,
      size: entry.file.size,
      order: entry.order,
    })),
  };

  const presignResponse = await api.post<PresignResponse>(
    apiRoutes.entities.mediaPresign(entityType, entityId),
    presignPayload,
  );
  const uploads = Array.isArray(presignResponse.data?.uploads)
    ? presignResponse.data.uploads
    : [];
  if (uploads.length !== mediaInputs.length) {
    throw new Error("Upload URL count mismatch.");
  }

  // Resolve intrinsic dimensions in parallel so the additional measurement
  // work does not extend the upload's wall-clock time meaningfully.
  const intrinsicByIndex = await Promise.all(
    mediaInputs.map((input) => resolveIntrinsic(input)),
  );

  for (let index = 0; index < uploads.length; index += 1) {
    const upload = uploads[index];
    const media = mediaInputs[index];
    const response = await fetch(upload.uploadUrl, {
      method: upload.method,
      headers: upload.headers,
      body: media.file,
    });
    if (!response.ok) {
      throw new Error(
        `Media upload failed for "${media.file.name}" with status ${response.status}.`,
      );
    }
  }

  const mediaFiles: FinalizeMediaPayloadEntry[] = uploads.map(
    (upload, index) => {
      const intrinsic = intrinsicByIndex[index];
      const fallbackFromInput = mediaInputs[index].intrinsic ?? null;
      return buildFinalizeEntry(upload, intrinsic ?? fallbackFromInput);
    },
  );

  await api.post(apiRoutes.entities.media(entityType, entityId), {
    mediaFiles,
  });
}

/**
 * Re-export for tests and callers that want to construct intrinsic
 * dimensions directly without owning the file picker.
 */
export const __test__ = { buildIntrinsicDimensions };
